import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {ScheduleService} from '../../services/schedule.service';
import {Router} from '@angular/router';
import {PersonLoginService} from '../../services/person-login-service';
import {EntitySearchCriteria} from '../../entity/entity-search-criteria';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {EntityHeader} from '../../entity/entity-header';
import {StringUtils} from '../../utils/string-utils';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {PersonLogin} from '../../person/person-login';
import {Entity} from '../../entity/entity';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import { DatePipe } from '@angular/common';
import {DialogService} from "../../services/dialog.service";

@Component({
    selector: 'app-project-list',
    templateUrl: './project-list.component.html',
    styleUrls: ['./project-list.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatProgressSpinner, MatPaginator, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, DatePipe]
})
export class ProjectListComponent implements OnInit {
  private readonly scheduleService = inject(ScheduleService);
  private readonly router = inject(Router);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly dialogService = inject(DialogService);


  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  private user!: PersonLogin;
  searchResults: MatTableDataSource<EntityHeader> = new MatTableDataSource<EntityHeader>();
  searchTerm!: string;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly warningMessage = signal<string>(undefined!);
  displayedColumns: string[] = ['entityId', 'projectName', 'totalUnitQty', 'locationCity', 'locationState', 'locationZip',
    'createTimestamp', 'createUser', 'updateTimestamp', 'updateUser'];

  ngOnInit(): void {
    this.user = this.personLoginService.getCachedLogin()!;
    this.search();
  }

  search(): void {
    this.clearStatus();
    const criteria: EntitySearchCriteria = new EntitySearchCriteria();
    criteria.personId = this.user.person.personId;
    criteria.searchTerm = this.searchTerm;
    this.scheduleService.search(criteria).subscribe({
      next: (results) => {
        this.searchResults = new MatTableDataSource<EntityHeader>(results);
        this.searchResults.sort = this.sort;
        this.searchResults.paginator = this.paginator;
        this.searchResults.sortingDataAccessor = this.tableDataAccessor();
        if (results.length === 200) {
          this.warningMessage.set('These results might not include some older entries, please provide more criteria to shorten the list');
        }
      },
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)});
  }

  private tableDataAccessor(): (item: EntityHeader, property: string) => any {
    return function (item: EntityHeader, property: string): any {
      switch (property) {
        case 'entityId':
          return item.id.entityId;
        default:
          return (item as unknown as Record<string, unknown>)[property];
      }
    };
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.warningMessage.set(null!);
    this.searchResults = null!;
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  rowSelected(schedule: EntityHeader): void {
    this.navigateToEntity(schedule.id.entityId);
  }

  private navigateToEntity(entityId: string) {
    this.router.navigate(['schedules', entityId]);
  }

  createSchedule(): void {
    this.errorMessage.set(undefined!);
    this.dialogService.showAddScheduleDialog().subscribe({
      next: (input: Entity) => {
        if (input && input.projectName.trim().length) {
          this.isBusy.set(true);
          this.scheduleService.createSchedule(input).subscribe({
            next: (schedule) => this.router.navigate(['schedules', schedule.id.entityId]),
            error: (error) => this.handleError(error),
          });
        }
      }, error: (error) => this.handleError(error)
    });
  }

}
